import type { ReservationDetailDto, ReservationSummaryDto } from '@/services/platform/api/model';

/**
 * The exact `serviceType` an event registration carries — `ReservationConstants.ServiceTypes.Event`
 * on the backend, copied verbatim into both DTOs by `ReservationMapper`. Compared ordinally there,
 * so the casing is significant here too.
 */
export const EVENT_SERVICE_TYPE = 'Event';

/**
 * The event title of an event booking, or `null` for every other service type.
 *
 * An event registration has no venue-shaped identity: `venue` is either the fully-resolved venue
 * (the room it happens in) or `null` — `ReservationMapper.VenueNameOnly` deliberately suppresses the
 * name-only fallback for events. So the only thing that says *which event* the member booked is the
 * title, and the booking surfaces have to reach for it explicitly rather than titling the card with
 * the venue.
 *
 * Two sources, in order:
 * - `event.title` — detail DTO only, read live from the Event row at map time.
 * - `name` — the snapshot written at registration (`booking.Name = write.Event.Title`). It is the
 *   only source on the summary DTO (lists and the Home carousel), and also the only one on the
 *   share-code detail path, whose query does not `Include` `EventDetail` and so sends `event: null`.
 *
 * Returning `null` for non-events lets each call site keep its existing title with a plain `??`.
 */
export function eventTitle(reservation: ReservationSummaryDto | ReservationDetailDto): string | null {
  if (reservation.serviceType !== EVENT_SERVICE_TYPE) return null;
  const detail = reservation as ReservationDetailDto;
  return detail.event?.title ?? reservation.name ?? null;
}
